/**
 * Registry de cursos de PV-Learn.
 *
 * Descubre dinámicamente todos los cursos del repo via `import.meta.glob`
 * sobre `cursos/*\/course.yaml` y construye un mapa
 * `Map<slug, CourseData>`. Cada `CourseData` contiene metadatos del
 * curso (título, idioma, branding) y la lista de módulos derivada de
 * los `module.yaml` de cada paquete.
 *
 * Sin dependencia de React. Pensado para alimentar:
 *
 *   - `contexts/CourseContext.tsx` (curso activo según URL `:slug`).
 *   - `pages/CatalogPage.tsx` (si en el futuro se muestra un catálogo).
 *   - `lib/storage.ts` (storage keys prefijadas por slug).
 *
 * Limitación de Vite: los globs exigen literales, así que el patrón es
 * `../../../cursos/*\/...`. Cualquier curso nuevo bajo `cursos/<slug>/`
 * que cumpla la spec se incorpora automáticamente al registry — eso es
 * lo que hace "plug-and-play real".
 */


/* ------------------------------- Tipos ------------------------------- */

export type ModuleStatus = 'producido' | 'en_revision' | 'pendiente'

export interface CourseArea {
  id: number
  nombre: string
  nombreEs: string
  pesoExamen: number
  modulos: number[]
}

export interface CourseModule {
  id: number
  slug: string
  titulo: string
  duracionMin: number
  areaExamen: number
  estado: ModuleStatus
  faseProduccion: number
  /** Preguntas declaradas para el examen final (solo informativo). */
  preguntas: number
}

export interface CourseBranding {
  colorPrimario: string
  colorAcento: string
  logoClaro?: string
  favicon?: string
}

export interface CourseData {
  slug: string
  title: string
  shortTitle: string
  description: string
  language: string
  editor: string
  totalMinutes: number
  examMinutes: number
  examPassPct: number
  examTotalQuestions: number
  branding: CourseBranding
  areas: CourseArea[]
  modules: CourseModule[]
  /**
   * Categorías del curso para filtrado en el catálogo. Lista cerrada
   * en `COURSE_CATEGORIES` para que el filtro sea consistente entre
   * cursos. Un curso puede pertenecer a varias categorías (p.ej.
   * "Agent 365" cae en `ia-generativa` y `microsoft-365`).
   *
   * Si el `course.yaml` no declara nada, el curso queda en
   * `['otros']` para que siga visible en el catálogo sin perderse.
   */
  categorias: CourseCategory[]
  /**
   * Slug del módulo que contiene el examen final. Puede no existir si el
   * curso no tiene examen (cursos formativos sin certificación).
   */
  examModuleSlug: string | null
  certificateTitle: string
  certificateLegalName: string
  certificateEnabled: boolean
}

/* ------------------------------ Categorías ------------------------------ */

/**
 * Categorías canónicas del catálogo de PV-Learn. Mantenemos una lista
 * cerrada (vs strings libres) para que el filtro del catálogo sea
 * estable y los chips no se multipliquen sin control. Añadir una
 * categoría = editar este array + traducir en `CATEGORY_LABELS`.
 */
export const COURSE_CATEGORIES = [
  'microsoft-365',
  'ia-generativa',
  'agentes-ia',
  'gobernanza',
  'power-platform',
  'productividad',
  'otros',
] as const

export type CourseCategory = (typeof COURSE_CATEGORIES)[number]

export const CATEGORY_LABELS: Record<CourseCategory, string> = {
  'microsoft-365': 'Microsoft 365',
  'ia-generativa': 'IA generativa',
  'agentes-ia': 'Agentes de IA',
  gobernanza: 'Gobernanza y seguridad',
  'power-platform': 'Power Platform',
  productividad: 'Productividad',
  otros: 'Otros',
}

function normalizeCategorias(raw: unknown): CourseCategory[] {
  if (!Array.isArray(raw) || raw.length === 0) return ['otros']
  const valid = raw
    .filter((c): c is string => typeof c === 'string')
    .map(c => c.trim())
    .filter((c): c is CourseCategory =>
      (COURSE_CATEGORIES as readonly string[]).includes(c),
    )
  // Deduplicar manteniendo orden de declaración.
  const seen = new Set<CourseCategory>()
  const out: CourseCategory[] = []
  for (const c of valid) {
    if (!seen.has(c)) {
      seen.add(c)
      out.push(c)
    }
  }
  return out.length > 0 ? out : ['otros']
}

/* ----------------------------- Discovery ----------------------------- */

interface CourseYaml {
  spec_version: string
  id: string
  slug: string
  nombre: string
  nombre_corto: string
  descripcion_corta: string
  idioma: string
  editor: string
  duracion_total_min: number
  /** Lista de categorías canónicas para el catálogo. */
  categorias?: string[]
  branding?: {
    color_primario?: string
    color_acento?: string
    logo_claro?: string
    favicon?: string
  }
  areas_examen?: Array<{
    id: number
    nombre_es: string
    nombre_en: string
    peso_pct: number
    modulos: number[]
  }>
  examen_final?: {
    duracion_min: number
    numero_preguntas: number
    puntaje_aprobado_pct: number
  }
  certificado?: {
    habilitado: boolean
    nombre_otorgado?: string
  }
  modulos: string[]
}

interface ModuleYaml {
  id: number
  slug: string
  titulo: string
  duracion_min: number
  area_examen: number
  estado: ModuleStatus
  fase_produccion: number
  preguntas_aporta_examen_final?: number
}

// `import.meta.glob(...)` es una macro que Vite transforma EN BUILD a un
// objeto literal con los imports ya resueltos. OJO: Vite reemplaza la
// LLAMADA, pero NO deja ninguna función `import.meta.glob` en runtime. Por
// eso NO se puede guardar con `typeof import.meta.glob === 'function'`: en el
// navegador ese typeof es 'undefined', el guard daría siempre false y el
// registry quedaría vacío (era el bug de "registry vacío en el cliente").
//
// La llamada va directa (Vite la inlinea) envuelta en try/catch solo para
// ejecutar este módulo en Node sin Vite (scripts tsx), donde la macro no se
// transforma y la llamada lanzaría; ahí se queda en {}.

// Sin `?raw`: el complemento mw-yaml-como-json los convierte al
// compilar, asi que aqui llegan ya como objeto y el parser de YAML
// no viaja al navegador.
let courseFiles: Record<string, unknown> = {}
try {
  courseFiles = import.meta.glob(
    '../../../cursos/*/course.yaml',
    { import: 'default', eager: true },
  ) as Record<string, unknown>
} catch {
  // Node sin Vite: import.meta.glob no existe; se queda en {}.
}

let moduleFiles: Record<string, unknown> = {}
try {
  moduleFiles = import.meta.glob(
    '../../../cursos/*/modulos/*/module.yaml',
    { import: 'default', eager: true },
  ) as Record<string, unknown>
} catch {
  // Node sin Vite: import.meta.glob no existe; se queda en {}.
}

/**
 * Carga eager de los assets de marca de cada curso
 * (`cursos/<slug>/assets/*`). Vite bundlea cada archivo y devuelve la
 * URL final servible. Patrón idéntico al de assets de módulos en
 * `lib/content.ts`.
 *
 * Esto permite que cada curso traiga sus propias imágenes (logos,
 * favicons, hero) sin tener que copiarlas manualmente a
 * `platform/public/`. El `course.yaml` declara solo el nombre de
 * fichero (`branding.logo_claro: agent365-logo-256.png`); el registry
 * resuelve a la URL bundleada en build.
 *
 * NB: solo PNG/SVG/JPG/WEBP. Otros formatos los ignoramos.
 */
let courseAssetFiles: Record<string, string> = {}
try {
  courseAssetFiles = import.meta.glob<string>(
    '../../../cursos/*/assets/*.{png,svg,jpg,jpeg,webp}',
    { query: '?url', import: 'default', eager: true },
  ) as Record<string, string>
} catch {
  // Node sin Vite: import.meta.glob no existe; se queda en {}.
}

/* ---------------------------- Construcción ---------------------------- */

/**
 * Devuelve el contenido de un fichero de curso.
 *
 * En el navegador llega ya convertido por el complemento
 * mw-yaml-como-json, asi que basta con devolverlo. Solo hace falta
 * parsear cuando este modulo corre en Node sin Vite, en los scripts
 * de comprobacion.
 */
function safeParse<T>(entrada: unknown, path: string): T | null {
  if (entrada && typeof entrada === 'object') {
    return entrada as T
  }
  // Si llega texto, es que el complemento no lo transformo: solo
  // pasa en scripts fuera de Vite, y ahi el registro no se usa.
  if (typeof entrada === 'string') {
    console.warn('[coursesRegistry] fichero sin transformar:', path)
  }
  return null
}

/**
 * Resuelve el nombre de un asset declarado en `branding.*` (p. ej.
 * "agent365-logo-256.png") a su URL bundleada por Vite. Busca primero
 * el match exacto del path completo (`cursos/<slug>/assets/<name>`);
 * si no encuentra (por ej. el archivo se llama distinto), devuelve
 * `undefined` para que el consumidor caiga al fallback.
 *
 * NB: el match se hace por sufijo, no por igualdad. El glob expone
 * paths relativos del estilo
 * `../../../cursos/agent365-cert/assets/agent365-logo-256.png` y aquí
 * solo tenemos `(slug, name)`.
 */
function resolveCourseAsset(slug: string, filename: string | undefined): string | undefined {
  if (!filename) return undefined
  const target = `cursos/${slug}/assets/${filename}`
  for (const [path, url] of Object.entries(courseAssetFiles)) {
    if (path.endsWith(target)) return url
  }
  return undefined
}

function buildRegistry(): Map<string, CourseData> {
  const registry = new Map<string, CourseData>()

  // 1) Cargar course.yaml de cada curso.
  for (const [path, raw] of Object.entries(courseFiles)) {
    // Convención: cualquier directorio bajo cursos/ cuyo nombre empiece
    // por '_' (p. ej. cursos/_template/) se ignora — es contenido
    // interno como plantillas o ejemplos, no cursos publicables.
    if (path.includes('/_')) continue
    const match = path.match(/cursos\/([^/]+)\/course\.yaml$/)
    if (!match) continue
    const slug = match[1]
    const cy = safeParse<CourseYaml>(raw, path)
    if (!cy) continue

    const areas: CourseArea[] = (cy.areas_examen ?? []).map(a => ({
      id: a.id,
      nombre: a.nombre_en,
      nombreEs: a.nombre_es,
      pesoExamen: a.peso_pct,
      modulos: a.modulos,
    }))

    const examTotal = cy.examen_final?.numero_preguntas ?? 0
    const examMinutes = cy.examen_final?.duracion_min ?? 90
    const examPassPct = cy.examen_final?.puntaje_aprobado_pct ?? 70

    // Resolución de assets de marca: si el course.yaml declara
    // `branding.logo_claro: foo.png`, buscamos `cursos/<slug>/assets/foo.png`
    // y reemplazamos el nombre por la URL bundleada. Si no existe el
    // asset (o el campo no se ha declarado), dejamos `undefined` y el
    // componente que lo consume cae al fallback.
    const logoClaro = resolveCourseAsset(slug, cy.branding?.logo_claro)
    const favicon = resolveCourseAsset(slug, cy.branding?.favicon)

    const courseData: CourseData = {
      slug,
      title: cy.nombre,
      shortTitle: cy.nombre_corto,
      description: cy.descripcion_corta,
      language: cy.idioma,
      editor: cy.editor,
      totalMinutes: cy.duracion_total_min,
      examMinutes,
      examPassPct,
      examTotalQuestions: examTotal,
      branding: {
        colorPrimario: cy.branding?.color_primario ?? '#9A44E5',
        colorAcento: cy.branding?.color_acento ?? '#F68DAC',
        logoClaro,
        favicon,
      },
      areas,
      modules: [], // se rellena en el segundo paso
      categorias: normalizeCategorias(cy.categorias),
      examModuleSlug: null,
      certificateTitle: cy.certificado?.nombre_otorgado ?? cy.nombre,
      certificateLegalName: cy.nombre,
      certificateEnabled: cy.certificado?.habilitado ?? false,
    }
    registry.set(slug, courseData)
  }

  // 2) Cargar module.yaml de cada módulo y asignarlo al curso correspondiente.
  for (const [path, raw] of Object.entries(moduleFiles)) {
    if (path.includes('/_')) continue
    const match = path.match(/cursos\/([^/]+)\/modulos\/([^/]+)\/module\.yaml$/)
    if (!match) continue
    const courseSlug = match[1]
    const my = safeParse<ModuleYaml>(raw, path)
    if (!my) continue
    const course = registry.get(courseSlug)
    if (!course) continue

    course.modules.push({
      id: my.id,
      slug: my.slug,
      titulo: my.titulo,
      duracionMin: my.duracion_min,
      areaExamen: my.area_examen,
      estado: my.estado,
      faseProduccion: my.fase_produccion,
      preguntas: my.preguntas_aporta_examen_final ?? 0,
    })
  }

  // 3) Ordenar módulos por id y derivar el examModuleSlug si existe
  //    (areaExamen === 0). En el modelo actual el examen es un módulo
  //    "virtual" final; si el yaml no lo marca, se busca el último.
  for (const course of registry.values()) {
    course.modules.sort((a, b) => a.id - b.id)
    const exam = course.modules.find(m => m.areaExamen === 0)
    if (exam) course.examModuleSlug = exam.slug
  }

  return registry
}

const REGISTRY = buildRegistry()

/* ------------------------------- API ------------------------------- */

export function listCourses(): CourseData[] {
  return [...REGISTRY.values()]
}

/**
 * Slugs de todos los cursos descubiertos. Útil para cargas eager
 * top-level en otros módulos (exam.ts, quiz.ts, labs.ts, resources.ts)
 * que necesitan indexar el contenido por curso al import.
 */
export function listCourseSlugs(): string[] {
  return [...REGISTRY.keys()]
}

export function getCourse(slug: string): CourseData | undefined {
  return REGISTRY.get(slug)
}

/** Devuelve el primer curso disponible. Útil como redirect default. */
export function defaultCourseSlug(): string {
  const first = REGISTRY.keys().next().value
  return first ?? 'mentor-wallet'
}

/**
 * Estado interno del registry para diagnóstico. Útil cuando
 * `listCourses()` devuelve vacío y queremos saber por qué: ¿el glob
 * no encontró nada? ¿los YAMLs no parsean? ¿no había module.yaml?
 *
 * Se expone también en `window.__pvRegistryDebug` para inspección
 * desde la consola del navegador.
 */
export interface RegistryDebug {
  courseFilePaths: string[]
  moduleFilePaths: string[]
  registrySlugs: string[]
  moduleCountBySlug: Record<string, number>
}

export function registryDebug(): RegistryDebug {
  const slugs = [...REGISTRY.keys()]
  const moduleCountBySlug: Record<string, number> = {}
  for (const s of slugs) {
    moduleCountBySlug[s] = REGISTRY.get(s)?.modules.length ?? 0
  }
  return {
    courseFilePaths: Object.keys(courseFiles),
    moduleFilePaths: Object.keys(moduleFiles),
    registrySlugs: slugs,
    moduleCountBySlug,
  }
}

// Exponer en window para debug fácil desde la consola del navegador.
if (typeof window !== 'undefined') {
  ;(window as unknown as { __pvRegistryDebug?: () => RegistryDebug }).__pvRegistryDebug =
    registryDebug
}

/* ------------------------- helpers derivados ------------------------- */

/** Módulos de contenido del curso (excluye el examen final). */
export function contentModules(course: CourseData): CourseModule[] {
  return course.modules.filter(m => m.areaExamen !== 0)
}

/**
 * El titulo de un modulo a partir de su slug.
 *
 * Quitar los guiones a un slug pierde las tildes y deja mayusculas
 * donde no van: "03-eliminacion-de-deudas" salia como "Eliminacion De
 * Deudas". Se busca el titulo de verdad en el curso y solo se cae al
 * slug si el modulo ya no esta en el temario.
 *
 * Existe porque el mismo apaño estaba copiado en cuatro pantallas.
 */
export function tituloDeModulo(courseSlug: string, moduleSlug: string): string {
  const curso = getCourse(courseSlug)
  const encontrado = curso?.modules.find(m => m.slug === moduleSlug)
  if (encontrado) return encontrado.titulo

  const sinNumero = moduleSlug.replace(/^\d+-/, '').replace(/-/g, ' ')
  return sinNumero.charAt(0).toUpperCase() + sinNumero.slice(1)
}

/** Módulo del examen final (puede no existir). */
export function examModule(course: CourseData): CourseModule | null {
  return course.modules.find(m => m.areaExamen === 0) ?? null
}

export function findModule(course: CourseData, id: number | string): CourseModule | undefined {
  const numId = typeof id === 'string' ? parseInt(id, 10) : id
  return course.modules.find(m => m.id === numId)
}

export function getAreaForModule(course: CourseData, moduleId: number): CourseArea | undefined {
  return course.areas.find(a => a.modulos.includes(moduleId))
}

export function formatDuration(minutes: number): string {
  if (minutes < 60) return `${minutes} min`
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  return m === 0 ? `${h}h` : `${h}h ${m}min`
}
